import { useApp } from 'contexts/app';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useNavigationGuard } from 'contexts/navigationGuard';
import { useSnackbar } from 'contexts/snackbar';
import { isEqual, pick } from 'lodash';
import { createContext, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import apiService from 'services/api';
import assignmentService from 'services/assignment';
import useBeforeUnload from 'hooks/useBeforeUnload';
import submissionService from 'services/submission';
import {
    CITATION_SELECT_OPTIONS,
    DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
    DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
    DEFAULT_FEEDBACK_STRUCTURE,
    DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
    DEFAULT_FEEDBACK_TONE,
    DEFAULT_INLINE_COMMENTS_COMMENT_LENGTH,
    DEFAULT_INLINE_COMMENTS_LANGUAGE_LEVEL,
    DEFAULT_INLINE_COMMENTS_MAX_COMMENTS_PER_PAGE,
    DEFAULT_INLINE_COMMENTS_TONE,
} from 'utils/constants';
import { compareCanvasRubrics, hasD2lTextOnlyRubricMismatch } from 'utils/helper';
import { normalizeRubricScoringMode } from 'utils/rubricScoring';

const PageContext = createContext();

const PageProvider = (props) => {
    const { auth } = useAuth();
    const {
        course,
        setAssignments,
        isGradingFeatureDisabled,
        isFeedbackFeatureDisabled,
        isInlineCommentsFeatureDisabled,
        currentAssignment,
        previewSubmissions,
        setPreviewSubmissions,
        setStudentSubmissions,
    } = useApp();
    const { setDialog, closeDialog } = useDialog();
    const history = useHistory();
    const { onBeforeNavigate } = useNavigationGuard();
    const { message } = useSnackbar();
    const { t } = useTranslation('assignment');
    const [form, setForm] = useState(null);
    const [errors, setErrors] = useState({});
    const [selectRubricDialog, setSelectRubricDialog] = useState({ open: false });
    const [createRubricDialog, setCreateRubricDialog] = useState({ open: false });
    const [createRubricExistingDialog, setCreateRubricExistingDialog] = useState({ open: false });
    const [createRubricAIDialog, setCreateRubricAIDialog] = useState({ open: false });
    const [editRubricDialog, setEditRubricDialog] = useState({ open: false, customDefaultForm: null });
    const [uploadContentBankResourcesDialog, setUploadContentBankResourcesDialog] = useState({ open: false });
    const [editContentBankResourcesDialog, setEditContentBankResourcesDialog] = useState({ open: false });
    const [gradingSettingsDialog, setGradingSettingsDialog] = useState({ open: false });
    const [feedbackSettingsDialog, setFeedbackSettingsDialog] = useState({ open: false });
    const [studentFeedbackSettingsDialog, setStudentFeedbackSettingsDialog] = useState({ open: false });
    const [inlineCommentsSettingsDialog, setInlineCommentsSettingsDialog] = useState({ open: false });
    const [pushRubricDialog, setPushRubricDialog] = useState({ open: false });
    const [hasClickedSubmit, setHasClickedSubmit] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const validateAll = useCallback(() => {
        let isValid = true;
        let newErrors = {};

        if (!form.title.trim()) {
            isValid = false;
            newErrors = { ...newErrors, title: true };
        }

        if (!form.description.trim()) {
            isValid = false;
            newErrors = { ...newErrors, description: true };
        }

        if (!form.rubric) {
            isValid = false;
            newErrors = { ...newErrors, rubric: true };
        }

        if (errors.gradingSettings) {
            isValid = false;
            newErrors = { ...newErrors, gradingSettings: errors.gradingSettings };
        }

        if (errors.feedbackSettings) {
            isValid = false;
            newErrors = { ...newErrors, feedbackSettings: errors.feedbackSettings };
        }

        if (errors.studentFeedbackSettings) {
            isValid = false;
            newErrors = { ...newErrors, studentFeedbackSettings: errors.studentFeedbackSettings };
        }

        setErrors(newErrors);

        return {
            isValid,
            errors: newErrors,
        };
    }, [
        errors.feedbackSettings,
        errors.gradingSettings,
        errors.studentFeedbackSettings,
        form?.description,
        form?.rubric,
        form?.title,
    ]);

    const shouldPreviewReset = useCallback((oldAssignment, newAssignment) => {
        if (oldAssignment.has_finished_preview) return false;
        return !isEqual(
            pick(oldAssignment, [
                'title',
                'description',
                'rubric',
                'citation_format',
                'content_bank_resources',
                'ai_grading_enabled',
                'ai_grading_settings',
                'ai_grading_context',
                'ai_feedback_enabled',
                'ai_feedback_settings',
                'ai_feedback_context',
                'ai_inline_comments_enabled',
                'ai_inline_comments_settings',
                'ai_inline_comments_context',
            ]),
            pick(newAssignment, [
                'title',
                'description',
                'rubric',
                'citation_format',
                'content_bank_resources',
                'ai_grading_enabled',
                'ai_grading_settings',
                'ai_grading_context',
                'ai_feedback_enabled',
                'ai_feedback_settings',
                'ai_feedback_context',
                'ai_inline_comments_enabled',
                'ai_inline_comments_settings',
                'ai_inline_comments_context',
            ])
        );
    }, []);

    const shouldFormativeReset = useCallback((oldAssignment, newAssignment) => {
        if (oldAssignment.has_finished_formative) return false;
        return !isEqual(
            pick(oldAssignment, [
                'title',
                'description',
                'rubric',
                'citation_format',
                'content_bank_resources',
                'ai_grading_enabled',
                'ai_grading_settings',
                'ai_grading_context',
                'ai_feedback_enabled',
                'ai_feedback_settings',
                'ai_feedback_context',
                'ai_student_feedback_enabled',
                'ai_student_feedback_settings',
                'ai_student_feedback_context',
            ]),
            pick(newAssignment, [
                'title',
                'description',
                'rubric',
                'citation_format',
                'content_bank_resources',
                'ai_grading_enabled',
                'ai_grading_settings',
                'ai_grading_context',
                'ai_feedback_enabled',
                'ai_feedback_settings',
                'ai_feedback_context',
                'ai_student_feedback_enabled',
                'ai_student_feedback_settings',
                'ai_student_feedback_context',
            ])
        );
    }, []);

    const onClickBack = () => {
        setDialog({
            open: true,
            message: t('common:dialog.unsaved-changes.title', 'You Have Unsaved Changes'),
            description: t(
                'common:dialog.unsaved-changes.description',
                'If you exit without saving, all unsaved changes will be lost.'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('common:controls.exit', 'Exit'),
            cancelButtonText: t('common:controls.cancel', 'Cancel'),
            onAction: () => {
                history.push(`/assignments/${currentAssignment.id}`);
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const updateAssignment = useCallback(
        async (api_data, newRubric) => {
            setHasClickedSubmit(true);
            const { isValid, errors } = validateAll();
            if (!isValid) {
                return setDialog({
                    open: true,
                    message: t(
                        'dialog.incomplete-assignment-warning.title',
                        'Please complete all missing required fields before publishing.'
                    ),
                    description: (
                        <ul>
                            {errors.title && (
                                <li style={{ textAlign: 'left' }}>{t('assignment-title-label', 'Assignment title')}</li>
                            )}
                            {errors.description && (
                                <li style={{ textAlign: 'left' }}>
                                    {t('assignment-description-label', 'Assignment description')}
                                </li>
                            )}
                            {errors.rubric && (
                                <li style={{ textAlign: 'left' }}>
                                    {t('dialog.incomplete-assignment-warning.assignment-rubric', 'Assignment rubric')}
                                </li>
                            )}
                            {errors.gradingSettings && (
                                <li style={{ textAlign: 'left' }}>
                                    {t('dialog.incomplete-assignment-warning.grading-settings', 'Grading settings')}
                                </li>
                            )}
                            {errors.feedbackSettings && (
                                <li style={{ textAlign: 'left' }}>
                                    {t('dialog.incomplete-assignment-warning.feedback-settings', 'Feedback settings')}
                                </li>
                            )}
                            {errors.studentFeedbackSettings && (
                                <li style={{ textAlign: 'left' }}>
                                    {t(
                                        'dialog.incomplete-assignment-warning.formative-feedback-settings',
                                        'Formative feedback settings'
                                    )}
                                </li>
                            )}
                        </ul>
                    ),
                    note: '',
                    variant: 'destructive',
                    actionButtonText: t('common:controls.ok', 'Ok'),
                    cancelButtonText: t('common:controls.cancel', 'Cancel'),
                    onAction: () => closeDialog(),
                    onCancel: () => closeDialog(),
                    onClose: () => closeDialog(),
                });
            }

            setIsSaving(true);
            const timestamp = new Date().getTime();
            const updatedAssignment = {
                modified_on: timestamp,
                course_id: course.id,
                title: form.title,
                description: form.description,
                rubric: newRubric ?? form.rubric,
                citation_format: form.citationFormat,
                content_bank_resources: form.contentBankResources,
                ai_grading_enabled: form.aiGradingEnabled && !isGradingFeatureDisabled,
                ai_grading_settings: form.gradingSettings,
                ai_grading_context: form.gradingCustomContext,
                ai_feedback_enabled: form.aiFeedbackEnabled && !isFeedbackFeatureDisabled,
                ai_feedback_settings: {
                    structure: form.feedbackSettings.structure,
                    num_suggestions: form.feedbackSettings.numSuggestions,
                    suggestion_length: form.feedbackSettings.suggestionLength,
                    language_level: form.feedbackSettings.languageLevel,
                    tone: form.feedbackSettings.tone,
                    templated: form.feedbackSettings.templated,
                    custom_instructions: form.feedbackSettings.customInstructions,
                },
                ai_feedback_context: form.feedbackCustomContext,
                ai_student_feedback_enabled: form.aiStudentFeedbackEnabled,
                ai_student_feedback_settings: {
                    structure: form.studentFeedbackSettings.structure,
                    num_suggestions: form.studentFeedbackSettings.numSuggestions,
                    suggestion_length: form.studentFeedbackSettings.suggestionLength,
                    language_level: form.studentFeedbackSettings.languageLevel,
                    tone: form.studentFeedbackSettings.tone,
                    templated: form.studentFeedbackSettings.templated,
                    custom_instructions: form.studentFeedbackSettings.customInstructions,
                },
                ai_student_feedback_context: form.studentFeedbackCustomContext,
                ai_inline_comments_enabled: form.aiInlineCommentsEnabled && !isInlineCommentsFeatureDisabled,
                ai_inline_comments_settings: {
                    max_comments_per_page: form.inlineCommentsSettings.maxCommentsPerPage,
                    comment_length: form.inlineCommentsSettings.commentLength,
                    language_level: form.inlineCommentsSettings.languageLevel,
                    tone: form.inlineCommentsSettings.tone,
                },
                ai_inline_comments_context: form.inlineCommentsCustomContext,
                is_draft: false,
                has_seen_preview: currentAssignment.has_seen_preview,
                has_finished_preview: currentAssignment.has_finished_preview,
                preview_files: currentAssignment.preview_files || [],
            };
            if (form?.api_assignment_id) {
                updatedAssignment.api_assignment_id = form.api_assignment_id;
                let new_api_data = api_data || form.api_data || currentAssignment.api_data;
                new_api_data = { ...new_api_data, id: form.api_assignment_id };
                updatedAssignment.api_data = new_api_data;
            }
            if (shouldPreviewReset(currentAssignment, updatedAssignment)) {
                updatedAssignment.has_seen_preview = false;
                updatedAssignment.preview_files = updatedAssignment.preview_files.map((pf) => {
                    const previewSubmissionToDelete = previewSubmissions.find(
                        (ps) => ps.files[0].key === pf.data.key || ps.files[0].key.includes(pf.data.key)
                    );
                    if (previewSubmissionToDelete) {
                        setPreviewSubmissions((current) => {
                            return current.filter((ps) => ps.id !== previewSubmissionToDelete.id);
                        });
                        submissionService.deleteSubmission(previewSubmissionToDelete.id);
                    }
                    return {
                        ...pf,
                        is_preview_up_to_date: false,
                    };
                });
            }
            if (shouldFormativeReset(currentAssignment, updatedAssignment)) {
                await Promise.all([
                    assignmentService.deleteAssignmentFormatives(currentAssignment.id),
                    assignmentService.deleteAssignmentSubmissions(
                        currentAssignment.id,
                        JSON.stringify({ type: 'formative' })
                    ),
                ]);
                setStudentSubmissions([]);
            }
            await assignmentService.updateAssignment(currentAssignment.id, updatedAssignment);
            setAssignments((current) => {
                return current.map((a) => {
                    return a.id !== currentAssignment.id ? a : { ...a, ...updatedAssignment };
                });
            });
            message.success(t('message.update-success', 'Your assignment was successfully updated.'));
            history.push(`/assignments/${currentAssignment.id}`);
        },
        [
            closeDialog,
            course.id,
            currentAssignment,
            form?.aiFeedbackEnabled,
            form?.aiGradingEnabled,
            form?.aiInlineCommentsEnabled,
            form?.aiStudentFeedbackEnabled,
            form?.api_assignment_id,
            form?.api_data,
            form?.citationFormat,
            form?.contentBankResources,
            form?.description,
            form?.feedbackCustomContext,
            form?.feedbackSettings,
            form?.gradingCustomContext,
            form?.gradingSettings,
            form?.inlineCommentsCustomContext,
            form?.inlineCommentsSettings,
            form?.rubric,
            form?.studentFeedbackCustomContext,
            form?.studentFeedbackSettings,
            form?.title,
            history,
            isFeedbackFeatureDisabled,
            isGradingFeatureDisabled,
            isInlineCommentsFeatureDisabled,
            message,
            previewSubmissions,
            setAssignments,
            setDialog,
            setPreviewSubmissions,
            setStudentSubmissions,
            shouldFormativeReset,
            shouldPreviewReset,
            t,
            validateAll,
        ]
    );

    const unpublishAssignment = useCallback(
        async (api_data, newRubric) => {
            setIsSaving(true);
            const timestamp = new Date().getTime();
            const updatedAssignment = {
                modified_on: timestamp,
                course_id: course.id,
                title: form.title,
                description: form.description,
                rubric: newRubric ?? form.rubric,
                citation_format: form.citationFormat,
                content_bank_resources: form.contentBankResources,
                ai_feedback_enabled: form.aiFeedbackEnabled,
                ai_feedback_settings: {
                    structure: form.feedbackSettings.structure,
                    num_suggestions: form.feedbackSettings.numSuggestions,
                    suggestion_length: form.feedbackSettings.suggestionLength,
                    language_level: form.feedbackSettings.languageLevel,
                    tone: form.feedbackSettings.tone,
                    templated: form.feedbackSettings.templated,
                    custom_instructions: form.feedbackSettings.customInstructions,
                },
                ai_feedback_context: form.feedbackCustomContext,
                ai_grading_enabled: form.aiGradingEnabled,
                ai_grading_settings: form.gradingSettings,
                ai_grading_context: form.gradingCustomContext,
                ai_student_feedback_enabled: form.aiStudentFeedbackEnabled,
                ai_student_feedback_settings: {
                    structure: form.studentFeedbackSettings.structure,
                    num_suggestions: form.studentFeedbackSettings.numSuggestions,
                    suggestion_length: form.studentFeedbackSettings.suggestionLength,
                    language_level: form.studentFeedbackSettings.languageLevel,
                    tone: form.studentFeedbackSettings.tone,
                    templated: form.studentFeedbackSettings.templated,
                    custom_instructions: form.studentFeedbackSettings.customInstructions,
                },
                ai_student_feedback_context: form.studentFeedbackCustomContext,
                ai_inline_comments_enabled: form.aiInlineCommentsEnabled,
                ai_inline_comments_settings: {
                    max_comments_per_page: form.inlineCommentsSettings.maxCommentsPerPage,
                    comment_length: form.inlineCommentsSettings.commentLength,
                    language_level: form.inlineCommentsSettings.languageLevel,
                    tone: form.inlineCommentsSettings.tone,
                },
                ai_inline_comments_context: form.inlineCommentsCustomContext,
                is_draft: true,
                has_seen_preview: currentAssignment.has_seen_preview,
                has_finished_preview: currentAssignment.has_finished_preview,
                preview_files: currentAssignment.preview_files,
            };
            if (form?.api_assignment_id) {
                updatedAssignment.api_assignment_id = form.api_assignment_id;
                const newApiData = api_data || form.api_data || currentAssignment.api_data;
                updatedAssignment.api_data = { ...newApiData, id: form.api_assignment_id };
            }
            if (shouldPreviewReset(currentAssignment, updatedAssignment)) {
                updatedAssignment.has_seen_preview = false;
                updatedAssignment.preview_files = updatedAssignment.preview_files.map((pf) => {
                    const previewSubmissionToDelete = previewSubmissions.find(
                        (ps) => ps.files[0].key === pf.data.key || ps.files[0].key.includes(pf.data.key)
                    );
                    if (previewSubmissionToDelete) {
                        setPreviewSubmissions((current) => {
                            return current.filter((ps) => ps.id !== previewSubmissionToDelete.id);
                        });
                        submissionService.deleteSubmission(previewSubmissionToDelete.id);
                    }
                    return {
                        ...pf,
                        is_preview_up_to_date: false,
                    };
                });
            }
            await assignmentService.updateAssignment(currentAssignment.id, updatedAssignment);
            setAssignments((current) => {
                return current.map((a) => {
                    return a.id !== currentAssignment.id ? a : { ...a, ...updatedAssignment };
                });
            });
            message.success(t('message.update-success', 'Your assignment was successfully updated.'));
            history.push(`/assignments/${currentAssignment.id}`);
        },
        [
            course.id,
            currentAssignment,
            form?.aiFeedbackEnabled,
            form?.aiGradingEnabled,
            form?.aiInlineCommentsEnabled,
            form?.aiStudentFeedbackEnabled,
            form?.api_assignment_id,
            form?.api_data,
            form?.citationFormat,
            form?.contentBankResources,
            form?.description,
            form?.feedbackCustomContext,
            form?.feedbackSettings.customInstructions,
            form?.feedbackSettings.languageLevel,
            form?.feedbackSettings.numSuggestions,
            form?.feedbackSettings.structure,
            form?.feedbackSettings.suggestionLength,
            form?.feedbackSettings.templated,
            form?.feedbackSettings.tone,
            form?.gradingCustomContext,
            form?.gradingSettings,
            form?.inlineCommentsCustomContext,
            form?.inlineCommentsSettings.commentLength,
            form?.inlineCommentsSettings.languageLevel,
            form?.inlineCommentsSettings.maxCommentsPerPage,
            form?.inlineCommentsSettings.tone,
            form?.rubric,
            form?.studentFeedbackCustomContext,
            form?.studentFeedbackSettings.customInstructions,
            form?.studentFeedbackSettings.languageLevel,
            form?.studentFeedbackSettings.numSuggestions,
            form?.studentFeedbackSettings.structure,
            form?.studentFeedbackSettings.suggestionLength,
            form?.studentFeedbackSettings.templated,
            form?.studentFeedbackSettings.tone,
            form?.title,
            history,
            message,
            previewSubmissions,
            setAssignments,
            setPreviewSubmissions,
            shouldPreviewReset,
            t,
        ]
    );

    const onClickSave = useCallback(
        async ({ key }) => {
            if (form.api_assignment_id && process.env.REACT_APP_KIND === 'D2L' && hasD2lTextOnlyRubricMismatch(form)) {
                return setDialog({
                    open: true,
                    message: t('dialog.d2l-textonly-rubric.title', 'Update this rubric in D2L'),
                    description: t(
                        'dialog.d2l-textonly-rubric.description',
                        'Linked text-only rubrics cannot be updated from TimelyGrader. Edit the rubric in D2L, then use Resync from LMS before saving or grading.'
                    ),
                    note: '',
                    variant: 'primary',
                    actionButtonText: t('common:controls.ok', 'OK'),
                    cancelButtonText: '',
                    onAction: () => closeDialog(),
                    onCancel: () => closeDialog(),
                    onClose: () => closeDialog(),
                });
            }
            if (key === 'publish') {
                if (!form.api_assignment_id || process.env.REACT_APP_KIND !== 'CANVAS') return updateAssignment();
                const { isValid, errors } = validateAll();
                if (!isValid) {
                    return setDialog({
                        open: true,
                        message: t(
                            'dialog.incomplete-assignment-warning.title',
                            'Please complete all missing required fields before publishing.'
                        ),
                        description: (
                            <ul>
                                {errors.title && (
                                    <li style={{ textAlign: 'left' }}>
                                        {t('assignment-title-label', 'Assignment title')}
                                    </li>
                                )}
                                {errors.description && (
                                    <li style={{ textAlign: 'left' }}>
                                        {t(
                                            'dialog.incomplete-assignment-warning.assignment-description',
                                            'Assignment description'
                                        )}
                                    </li>
                                )}
                                {errors.rubric && (
                                    <li style={{ textAlign: 'left' }}>
                                        {t(
                                            'dialog.incomplete-assignment-warning.assignment-rubric',
                                            'Assignment rubric'
                                        )}
                                    </li>
                                )}
                                {errors.gradingSettings && (
                                    <li style={{ textAlign: 'left' }}>
                                        {t('dialog.incomplete-assignment-warning.grading-settings', 'Grading settings')}
                                    </li>
                                )}
                                {errors.feedbackSettings && (
                                    <li style={{ textAlign: 'left' }}>
                                        {t(
                                            'dialog.incomplete-assignment-warning.feedback-settings',
                                            'Feedback settings'
                                        )}
                                    </li>
                                )}
                                {errors.studentFeedbackSettings && (
                                    <li style={{ textAlign: 'left' }}>
                                        {t(
                                            'dialog.incomplete-assignment-warning.formative-feedback-settings',
                                            'Formative feedback settings'
                                        )}
                                    </li>
                                )}
                            </ul>
                        ),
                        note: '',
                        variant: 'destructive',
                        actionButtonText: t('common:controls.ok', 'Ok'),
                        cancelButtonText: t('common:controls.cancel', 'Cancel'),
                        onAction: () => closeDialog(),
                        onCancel: () => closeDialog(),
                        onClose: () => closeDialog(),
                    });
                }
                setIsSaving(true);
                const apiAssignment = await apiService.getAssignment({
                    course_id: auth.course_id,
                    accessToken: localStorage.getItem('access_token'),
                    assignment_id: form.api_assignment_id,
                });
                const doesRubricMatch = compareCanvasRubrics(form, apiAssignment.rubric, apiAssignment.rubric_settings);
                if (doesRubricMatch) return updateAssignment();
                return setPushRubricDialog({
                    open: true,
                    form: { ...currentAssignment, ...form },
                    onSave: ({ api_data, rubric }) => {
                        if (rubric) updateAssignment(api_data, rubric);
                    },
                });
            }
            if (key === 'save') {
                if (!form.api_assignment_id || process.env.REACT_APP_KIND !== 'CANVAS') return unpublishAssignment();
                setIsSaving(true);
                const apiAssignment = await apiService.getAssignment({
                    course_id: auth.course_id,
                    accessToken: localStorage.getItem('access_token'),
                    assignment_id: form.api_assignment_id,
                });
                const doesRubricMatch = compareCanvasRubrics(form, apiAssignment.rubric, apiAssignment.rubric_settings);
                if (!doesRubricMatch && form.rubric) {
                    return setPushRubricDialog({
                        open: true,
                        form: { ...currentAssignment, ...form },
                        onSave: ({ api_data, rubric }) => {
                            if (rubric) unpublishAssignment(api_data, rubric);
                        },
                    });
                } else {
                    unpublishAssignment();
                }
            }
        },
        [
            auth.course_id,
            form,
            closeDialog,
            currentAssignment,
            setDialog,
            setPushRubricDialog,
            t,
            unpublishAssignment,
            updateAssignment,
            validateAll,
        ]
    );

    useEffect(() => {
        if (hasClickedSubmit) {
            validateAll();
        }
    }, [hasClickedSubmit, validateAll]);

    useEffect(() => {
        if (currentAssignment) {
            const assignmentForm = {
                generatedId: currentAssignment.generated_id ?? null,
                title: currentAssignment.title,
                description: currentAssignment.description,
                rubric: normalizeRubricScoringMode(currentAssignment),
                citationFormat: currentAssignment.citation_format ?? CITATION_SELECT_OPTIONS[0].key,
                contentBankResources: currentAssignment.content_bank_resources || [],
                aiGradingEnabled: currentAssignment.ai_grading_enabled,
                gradingSettings: currentAssignment.ai_grading_settings,
                gradingCustomContext: currentAssignment.ai_grading_context,
                aiFeedbackEnabled: currentAssignment.ai_feedback_enabled,
                feedbackSettings: currentAssignment.ai_feedback_settings
                    ? {
                          structure: currentAssignment.ai_feedback_settings.structure,
                          numSuggestions: currentAssignment.ai_feedback_settings.num_suggestions,
                          suggestionLength: currentAssignment.ai_feedback_settings.suggestion_length,
                          languageLevel: currentAssignment.ai_feedback_settings.language_level,
                          tone: currentAssignment.ai_feedback_settings.tone,
                          templated: currentAssignment.ai_feedback_settings.templated,
                          customInstructions: currentAssignment.ai_feedback_settings.custom_instructions,
                      }
                    : {
                          structure: DEFAULT_FEEDBACK_STRUCTURE,
                          numSuggestions: DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
                          suggestionLength: DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
                          languageLevel: DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
                          tone: DEFAULT_FEEDBACK_TONE,
                          templated: {},
                          customInstructions: '',
                      },
                feedbackCustomContext: currentAssignment.ai_feedback_context,
                aiStudentFeedbackEnabled: Boolean(currentAssignment.ai_student_feedback_enabled),
                studentFeedbackSettings: currentAssignment.ai_student_feedback_settings
                    ? {
                          structure: currentAssignment.ai_student_feedback_settings.structure,
                          numSuggestions: currentAssignment.ai_student_feedback_settings.num_suggestions,
                          suggestionLength: currentAssignment.ai_student_feedback_settings.suggestion_length,
                          languageLevel: currentAssignment.ai_student_feedback_settings.language_level,
                          tone: currentAssignment.ai_student_feedback_settings.tone,
                          templated: currentAssignment.ai_student_feedback_settings.templated,
                          customInstructions: currentAssignment.ai_student_feedback_settings.custom_instructions,
                      }
                    : {
                          structure: DEFAULT_FEEDBACK_STRUCTURE,
                          numSuggestions: DEFAULT_FEEDBACK_NUM_SUGGESTIONS,
                          suggestionLength: DEFAULT_FEEDBACK_SUGGESTION_LENGTH,
                          languageLevel: DEFAULT_FEEDBACK_LANGUAGE_LEVEL,
                          tone: DEFAULT_FEEDBACK_TONE,
                          templated: {},
                          customInstructions: '',
                      },
                studentFeedbackCustomContext: currentAssignment.ai_student_feedback_context || '',
                aiInlineCommentsEnabled: Boolean(currentAssignment.ai_inline_comments_enabled),
                inlineCommentsSettings: currentAssignment.ai_inline_comments_settings
                    ? {
                          maxCommentsPerPage: currentAssignment.ai_inline_comments_settings.max_comments_per_page,
                          commentLength: currentAssignment.ai_inline_comments_settings.comment_length,
                          languageLevel: currentAssignment.ai_inline_comments_settings.language_level,
                          tone: currentAssignment.ai_inline_comments_settings.tone,
                      }
                    : {
                          maxCommentsPerPage: DEFAULT_INLINE_COMMENTS_MAX_COMMENTS_PER_PAGE,
                          commentLength: DEFAULT_INLINE_COMMENTS_COMMENT_LENGTH,
                          languageLevel: DEFAULT_INLINE_COMMENTS_LANGUAGE_LEVEL,
                          tone: DEFAULT_INLINE_COMMENTS_TONE,
                      },
                inlineCommentsCustomContext: currentAssignment.ai_inline_comments_context || '',
            };
            if (currentAssignment.api_assignment_id) {
                assignmentForm.api_assignment_id = currentAssignment.api_assignment_id;
                assignmentForm.api_data = currentAssignment.api_data;
            }
            setForm(assignmentForm);
        }
    }, [currentAssignment]);

    useEffect(() => {
        onBeforeNavigate((navigate) => {
            setDialog({
                open: true,
                message: t('common:dialog.unsaved-changes.title', 'You Have Unsaved Changes'),
                description: t(
                    'common:dialog.unsaved-changes.description',
                    'If you exit without saving, all unsaved changes will be lost.'
                ),
                note: '',
                variant: 'destructive',
                actionButtonText: t('common:controls.exit', 'Exit'),
                cancelButtonText: t('common:controls.cancel', 'Cancel'),
                onAction: () => {
                    closeDialog();
                    navigate();
                },
                onCancel: () => closeDialog(),
                onClose: () => closeDialog(),
            });
        });

        return () => {
            onBeforeNavigate(null);
        };
    }, [closeDialog, onBeforeNavigate, setDialog, t]);

    useBeforeUnload();

    return (
        <PageContext.Provider
            value={{
                form,
                setForm,
                errors,
                setErrors,
                selectRubricDialog,
                setSelectRubricDialog,
                createRubricDialog,
                setCreateRubricDialog,
                createRubricExistingDialog,
                setCreateRubricExistingDialog,
                createRubricAIDialog,
                setCreateRubricAIDialog,
                editRubricDialog,
                setEditRubricDialog,
                uploadContentBankResourcesDialog,
                setUploadContentBankResourcesDialog,
                editContentBankResourcesDialog,
                setEditContentBankResourcesDialog,
                gradingSettingsDialog,
                setGradingSettingsDialog,
                feedbackSettingsDialog,
                setFeedbackSettingsDialog,
                studentFeedbackSettingsDialog,
                setStudentFeedbackSettingsDialog,
                inlineCommentsSettingsDialog,
                setInlineCommentsSettingsDialog,
                pushRubricDialog,
                setPushRubricDialog,
                isSaving,
                setIsSaving,
                onClickBack,
                onClickSave,
            }}
            {...props}
        />
    );
};

export { PageContext };
export default PageProvider;
